import React from 'react';

interface UnitMismatchWarningProps {
  testName: string;
  conversions: Array<{
    fromValue: number;
    fromUnit: string;
    toValue: number;
    toUnit: string;
    date: string;
    isMislabeled?: boolean;
    reason?: string;
  }>;
  warnings?: string[];
  onDismiss: () => void;
  onCorrect?: () => void;
}

export const UnitMismatchWarning: React.FC<UnitMismatchWarningProps> = ({
  testName,
  conversions,
  warnings,
  onDismiss,
  onCorrect
}) => {
  // Check if this is a likely mislabeling case (e.g., hemoglobin)
  const hasMislabeling = conversions.some(c => c.isMislabeled);
  const isHemoglobin = testName.toLowerCase().includes('hemoglobin') || 
                       testName.toLowerCase().includes('hgb') ||
                       testName.toLowerCase().includes('hb');
  return (
    <div className="fixed top-4 right-4 max-w-md bg-yellow-50 border border-yellow-200 rounded-lg shadow-lg p-4 z-50">
      <div className="flex items-start gap-3">
        <svg className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-yellow-900 mb-1">
            {hasMislabeling ? 
              `⚠️ Unit Mislabeling Detected for ${testName}` : 
              `Unit Conversion Applied for ${testName}`
            }
          </h3>
          
          <p className="text-sm text-yellow-700 mb-2">
            {hasMislabeling ? (
              <>
                <strong>Your lab appears to have mislabeled the units.</strong>
                {isHemoglobin && (
                  <span className="block mt-1">
                    Hemoglobin values under 20 are almost always in g/dL, not g/L. 
                    Normal range is 12-17 g/dL (or 120-170 g/L).
                  </span>
                )}
              </>
            ) : (
              "Different units were detected in your lab results. Values have been automatically converted for consistency."
            )}
          </p>
          
          {warnings && warnings.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded p-2 mb-2">
              <p className="text-xs font-semibold text-red-800 mb-1">Warnings:</p>
              {warnings.map((warning, idx) => (
                <p key={idx} className="text-xs text-red-700">{warning}</p>
              ))}
            </div>
          )}
          
          <div className="bg-yellow-100 rounded p-2 text-xs space-y-1">
            {conversions.slice(0, 3).map((conv, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <span className="text-gray-600">{conv.date}:</span>
                <span className="font-mono">
                  {conv.fromValue} {conv.fromUnit} → {conv.toValue.toFixed(1)} {conv.toUnit}
                  {conv.isMislabeled && (
                    <span className="ml-2 text-red-600 text-xs">(likely mislabeled)</span>
                  )}
                </span>
              </div>
            ))}
            {conversions.length > 3 && (
              <div className="text-gray-600 italic">
                ...and {conversions.length - 3} more conversions
              </div>
            )}
          </div>
          
          <div className="mt-3 flex items-center justify-between">
            <p className="text-xs text-yellow-600">
              ℹ️ {hasMislabeling ? 
                "Review your original report to confirm the correct units" : 
                "Always verify converted values match your original reports"
              }
            </p>
            <div className="flex gap-2">
              {onCorrect && hasMislabeling && (
                <button
                  onClick={onCorrect}
                  className="text-sm bg-yellow-600 text-white px-3 py-1 rounded hover:bg-yellow-700 font-medium"
                >
                  Correct Units
                </button>
              )}
              <button
                onClick={onDismiss}
                className="text-sm text-yellow-800 hover:text-yellow-900 font-medium"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};